import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import { ErrorHandler, LOCALE_ID, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { GlobalErrorHandler } from './shared/services/global-error-handler.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import DataService from './shared/services/data.service';
import { WarehouseDataService } from './shared/services/warehouse-data.service';
import { SharedModule } from './shared/shared.module';
import { AnnouncementBannerComponent } from './Modules/announcements/announcement-banner/announcement-banner.component';
import { AnnouncementGateComponent } from './Modules/announcements/announcement-gate/announcement-gate.component';
import { registerLocaleData } from '@angular/common';
import locale from '@angular/common/locales/fi';
import { AuthInterceptorService } from './shared/interceptors/auth-interceptor.service';
import { CacheInterceptor } from './shared/interceptors/cache.interceptor';
import { ProgressInterceptor } from './shared/interceptors/progress.interceptor';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { MAT_TIMEPICKER_DEFAULT_OPTIONS, MAT_TIME_LOCALE, TimeAdapter } from '@dhutaryan/ngx-mat-timepicker';
import { FinTimeAdapterService } from 'src/app/shared/adapters/fin-time-adapter.service';
import { MultiTranslateHttpLoader } from './shared/loaders/multi-translate-http-loader';
import { provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools, StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule, provideEffects } from '@ngrx/effects';
import { StoreRouterConnectingModule, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { appReducer } from './state/app.reducer';
import { weatherReducer } from './shared/components/weather/state/weather.reducer';
import { WeatherEffects } from './shared/components/weather/state/weather.effects';
import { AppEffects } from './state/app.effects';
import { WorkClassificationModule } from './Modules/worktime/work-classification/work-classification.module';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorIntl } from './shared/providers/mat-paginator.provider';
import { HeaderComponent } from './core/navigation/header/header.component';
import { SignoutRedirectCallbackComponent } from './Modules/Home/signout-redirect.callback-component';
import { SigninRedirectCallbackComponent } from './Modules/Home/signin-redirect-callback.component';
import { LicenseDialogComponent } from './core/license/license.component';
import { PrivacyComponent } from './core/privacy/privacy.component';
import { SafetyModule } from './Modules/Safety/safety.module';
import { QuillModule } from 'ngx-quill';
import { AnnouncementsWidgetComponent } from './Modules/announcements/announcements-widget/announcements-widget.component';
import { ServerStatusIndicatorComponent } from './shared/components/server-status-indicator/server-status-indicator.component';
import { ToolbarProjectSelectorComponent } from './core/navigation/toolbar-project-selector/toolbar-project-selector.component';
import { environment } from 'src/environments/environment';


registerLocaleData(locale);
export function HttpLoaderFactory(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/i18n/pipes/relative-time.', suffix: '.json' }
  ]);
}
@NgModule({
  declarations: [AppComponent,
    HeaderComponent,
    SignoutRedirectCallbackComponent,
    SigninRedirectCallbackComponent,
    LicenseDialogComponent,
    PrivacyComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    SharedModule,
    AnnouncementBannerComponent,
    AnnouncementGateComponent,
    // CoreModule,
    BrowserModule,
    AppRoutingModule,
    WorkClassificationModule,
    // PaceProductionModule,
  StoreModule.forRoot({ app: appReducer, router: routerReducer, weather: weatherReducer }),
    StoreDevtoolsModule.instrument({
      name: 'RIV4-DEVTOOLS',
      maxAge: 25,
      logOnly: !isDevMode(),
      connectInZone: true,
    }),
  EffectsModule.forRoot([AppEffects, WeatherEffects]),
    StoreRouterConnectingModule.forRoot(),
    // SafetyModule stays eager: it exports ri-safety-widget, rendered on the home page.
    // Every other feature module is lazy-loaded from app-routing.module.ts (2026-09-14) so
    // the main bundle no longer carries warehouse, worktime, billing, companies, work-types
    // and diary code for users who never open them.
    SafetyModule,
    AnnouncementsWidgetComponent,
    ServerStatusIndicatorComponent,
    ToolbarProjectSelectorComponent,
    QuillModule.forRoot({

      theme: 'snow'
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),

  ],
  providers: [
    // Clock-dial timepicker (@dhutaryan/ngx-mat-timepicker) replacing Angular Material's
    // own, which only offers a dropdown list. It declares the same selectors, so the
    // markup is unchanged; the minute step moves here from per-component [interval]
    // bindings. Root-level so standalone components find TimeAdapter wherever they render.
    //
    // isMeridiem false keeps the 24-hour face Finnish users expect.
    // FinTimeAdapterService instead of provideNativeDateTimeAdapter(): the native one
    // formats through the locale (fi-FI renders 7.00) but parses only a colon, so
    // typing back what the field shows is rejected. The subclass normalises first.
    { provide: TimeAdapter, useClass: FinTimeAdapterService },
    { provide: MAT_TIME_LOCALE, useValue: 'fi-FI' },
    {
      provide: MAT_TIMEPICKER_DEFAULT_OPTIONS,
      useValue: {
        // Every field here is required by MatTimepickerDefaultOptions. useValue is not
        // type-checked, so a partial object leaves the rest undefined rather than falling
        // back to defaults - which is how an earlier `isMeridiem: false` was accepted
        // silently and did nothing: the key does not exist, the option is `format`.
        mode: 'dial',
        openAs: 'popup',
        format: '24h',
        showToggleModeButton: true,
        minuteInterval: 15,
        orientation: 'vertical',
      },
    },

    // v18 removed TranslateModule; the service and its loader are providers now.
    // MultiTranslateHttpLoader is kept rather than swapped for the package's own
    // provideTranslateMultiHttpLoader: ours tolerates a missing resource per file,
    // and changing that is a behaviour change this migration should not smuggle in.
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),

    DataService,
    WarehouseDataService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: ProgressInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'fi-FI' },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  provideStore({ app: appReducer, router: routerReducer, weather: weatherReducer }),
    provideRouterStore(),
  provideEffects(AppEffects, WeatherEffects),
    provideStoreDevtools(
      {
        name: 'RIV4-DEVTOOLS',
        maxAge: 25,
        logOnly: !isDevMode(),
        connectInZone: true,
      }
    ),

  ],
})
export class AppModule {}
